import { useState, useCallback, useEffect, useRef } from 'react';
import { initDatabase, getFile, saveFile, addChangedFile } from '../services/storage';
import { listFiles, fetchFileContent } from '../services/api';
import { inlineCssInHtml } from '../services/editor';
import { normalizePath, getLanguageFromPath, isHtmlFile } from '../utils/path';
import { AUTH_CUSTOMIZER } from '../constants';

export function useFileManager(domain, initialFile, isPasswordPage) {
  // Database ref
  const dbRef = useRef(null);
  const prevBlobUrlRef = useRef(null);

  // File state
  const [fileList, setFileList] = useState([]);
  const [currentFile, setCurrentFile] = useState(initialFile);
  const [activeHtmlPage, setActiveHtmlPage] = useState(initialFile);
  const [code, setCode] = useState('');
  const [codeLanguage, setCodeLanguage] = useState('html');
  const [iframeSrc, setIframeSrc] = useState('');

  // Loading state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dbReady, setDbReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize database
  useEffect(() => {
    initDatabase()
      .then((db) => {
        dbRef.current = db;
        setDbReady(true);
      })
      .catch(err => setError(err));
  }, []);

  // Create blob URL for iframe (revoke previous to prevent memory leak)
  const createIframeSrc = useCallback((html) => {
    if (prevBlobUrlRef.current) {
      URL.revokeObjectURL(prevBlobUrlRef.current);
    }
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    prevBlobUrlRef.current = url;
    return url;
  }, []);

  // The HTML the preview was loaded from, so an AI edit can be diffed against it
  const previewHtmlRef = useRef('');

  const showPreview = useCallback((html) => {
    previewHtmlRef.current = html;
    setIframeSrc(createIframeSrc(html));
  }, [createIframeSrc]);

  // Fetch all files from backend and cache locally
  const fetchAllFiles = useCallback(async () => {
    if (!dbReady) return;

    setLoading(true);
    setError(null);

    try {
      let files = await listFiles(domain);

      // Filter based on page type
      files = isPasswordPage
        ? files.filter(f => f.Key === AUTH_CUSTOMIZER)
        : files.filter(f => f.Key !== AUTH_CUSTOMIZER);

      // Normalize keys
      files = files.map(f => ({ ...f, Key: normalizePath(f.Key) }));
      setFileList(files);

      // Fetch and cache each file
      for (const file of files) {
        try {
          const content = await fetchFileContent(file.Key, domain);
          await saveFile(file.Key, content);
          await saveFile(`${file.Key}-original`, content);
        } catch (err) {
          console.error(`Error fetching ${file.Key}:`, err);
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  }, [domain, isPasswordPage, dbReady]);

  // Load initial file after fetch completes
  useEffect(() => {
    const loadInitialFile = async () => {
      if (loading || !currentFile) return;

      const content = await getFile(currentFile);
      if (!content) return;

      setCode(content);
      setCodeLanguage(getLanguageFromPath(currentFile));

      if (isHtmlFile(currentFile)) {
        showPreview(await inlineCssInHtml(content));
      }
    };
    loadInitialFile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // Only run when loading changes from true to false

  // Switch to file
  const switchFile = useCallback(async (filename) => {
    const normalizedFile = normalizePath(filename);
    const content = await getFile(normalizedFile);

    setCurrentFile(normalizedFile);
    setCode(content || '');
    setCodeLanguage(getLanguageFromPath(normalizedFile));

    // Update iframe and activeHtmlPage for HTML files only
    if (isHtmlFile(normalizedFile) && content) {
      setActiveHtmlPage(normalizedFile);
      showPreview(await inlineCssInHtml(content));
    }
  }, [showPreview]);

  // Re-read the current file into the code editor after something else wrote it
  const refreshCode = useCallback(async () => {
    if (!currentFile) return;

    const content = await getFile(currentFile);
    if (!content) return;

    setCode(content);
  }, [currentFile]);

  // The previewed page as the iframe renders it: stylesheets inlined
  const getPreviewHtml = useCallback(
    async () => inlineCssInHtml(await getFile(activeHtmlPage)),
    [activeHtmlPage]
  );

  // Save file content
  const saveFileContent = useCallback(async (filename, content) => {
    setIsSaving(true);
    try {
      await saveFile(filename, content);
      addChangedFile(filename);

      if (filename === currentFile) {
        setCode(content);
      }
    } finally {
      setIsSaving(false);
    }
  }, [currentFile]);

  // Update code (local only, no save)
  const updateCode = useCallback((newCode) => {
    setCode(newCode);
  }, []);

  // Create new file
  const createNewFile = useCallback(async (filePath, content = '') => {
    const { validateFilePath, getFileTemplate } = await import('../utils/fileTemplates');
    const normalizedFile = normalizePath(filePath);

    const validation = validateFilePath(normalizedFile);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    if (fileList.find(f => f.Key === normalizedFile)) {
      throw new Error(`File ${normalizedFile} already exists`);
    }

    const fileContent = getFileTemplate(normalizedFile, content);
    await saveFile(normalizedFile, fileContent);
    await saveFile(`${normalizedFile}-original`, fileContent);

    const newFile = { Key: normalizedFile, Size: fileContent.length };
    setFileList(prev => [...prev, newFile]);
    addChangedFile(normalizedFile);

    return normalizedFile;
  }, [fileList]);

  return {
    // Refs
    dbRef,
    previewHtmlRef,

    // File state
    fileList,
    setFileList,
    currentFile,
    activeHtmlPage,
    code,
    codeLanguage,
    iframeSrc,

    // Loading state
    loading,
    error,
    dbReady,
    isSaving,

    // Actions
    fetchAllFiles,
    switchFile,
    refreshCode,
    getPreviewHtml,
    showPreview,
    saveFileContent,
    updateCode,
    createNewFile
  };
}
